import React, { useState, useEffect } from "react";
import { io, Socket } from "socket.io-client";
import { DndContext, useDraggable, DragOverlay } from '@dnd-kit/core';
import {
  getSessionsByDashboard,
  getSessionDashboardById,
  SessionDashboard,
} from "../../utils/api";
import { Session, Dog, PopulatedTrainer } from "../../types";
import { toast } from 'sonner';
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useAuth } from "../../contexts/AuthContext";
import { SessionsList } from "../session/SessionsList";
import {
  //Lucide-react icons
  ArrowLeft,
  Plus,
  CalendarDays,
  List,
  RefreshCw,
  Share2,
} from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";
import { useViewMode } from "../../contexts/ViewModeContext";
import { SessionForm } from "../session/SessionForm";
import { SessionCalendar } from "../session/SessionCalendar";
import { BatchSessionForm } from "../session/BatchSessionForm";
import { updateSession } from '../../utils/api';
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Input } from "../ui/input";

const DraggableDog = ({ dog }: { dog: Dog }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: dog._id,
    data: { dog },
  });
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes} className="p-2 bg-white border rounded-md flex items-center gap-3 shadow-sm cursor-grab">
      <Avatar className="h-8 w-8">
        <AvatarImage src={dog.profilePhoto} />
        <AvatarFallback>{dog.name.substring(0, 2)}</AvatarFallback>
      </Avatar>
      <span className="font-medium text-sm">{dog.name}</span>
    </div>
  );
};

const AvailableDogsList = ({ dogs }: { dogs: Dog[] }) => {
  const [filter, setFilter] = useState('');

  const filteredDogs = dogs.filter(dog =>
    dog.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Chiens Disponibles</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Input placeholder="Filtrer par nom..." value={filter} onChange={(e) => setFilter(e.target.value)} className="mb-4" />
        {filteredDogs.map(dog => <DraggableDog key={dog._id} dog={dog} />)}
      </CardContent>
    </Card>
  );
};

interface DashboardDetailProps {
  dashboardId: string;
  onBack: () => void;
  onNavigateToSession: (sessionId: string) => void;
}

export const DashboardDetail: React.FC<DashboardDetailProps> = ({
  dashboardId,
  onBack,
}) => {
  const { user } = useAuth();
  const { setSelectedDogId } = useViewMode();
  const [trainerDogs, setTrainerDogs] = useState<Dog[]>([]);
  const [dashboardTrainers, setDashboardTrainers] = useState<PopulatedTrainer[]>([]);
  const [dashboard, setDashboard] = useState<SessionDashboard | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBatchFormOpen, setIsBatchFormOpen] = useState(false);
  const [, setSocket] = useState<Socket | null>(null);
  const [isSessionFormOpen, setIsSessionFormOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState<Session | null>(null);
  const [initialDateForNewSession, setInitialDateForNewSession] = useState<
    Date | undefined
  >(undefined);  
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [viewType, setViewType] = useState<"list" | "calendar">("list");

  const fetchData = async () => {
    try {
      const [dashboardData, sessionsData] = await Promise.all([
        getSessionDashboardById(dashboardId),
        getSessionsByDashboard(dashboardId),
      ]);
      setDashboard(dashboardData);
      setDashboardTrainers(dashboardData.trainerIds as PopulatedTrainer[]);
      const allDogs = dashboardData.trainerIds.flatMap(t => t.userId?.trainedDogs?.filter((d): d is Dog => typeof d !== 'string' && !!d?._id) || []);
      const uniqueDogs = Array.from(new Map(allDogs.map(dog => [dog._id, dog])).values());
      setTrainerDogs(uniqueDogs);
      setSessions(sessionsData.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
    } catch (error) {
      toast.error("Impossible de charger les détails du tableau de bord.");
      console.error("Error fetching dashboard details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    const newSocket = io(process.env.REACT_APP_API_URL || "http://localhost:5000");
    setSocket(newSocket);
    newSocket.on("connect", () => {
      newSocket.emit("joinDashboardRoom", dashboardId);
    });

    newSocket.on("session:created", (newSession: Session) => {
      setSessions((prev) => {
        if (prev.some(s => s._id === newSession._id)) {
          return prev; // Already exists, do nothing.
        }
        toast.info("Nouvelle session ajoutée !");
        return [...prev, newSession].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
      });
    });
    newSocket.on("session:updated", (updatedSession: Session) => {
      setSessions((prev) => prev.map((s) => (s._id === updatedSession._id ? updatedSession : s)));
      toast.info(`Session "${updatedSession.location}" mise à jour.`);
    });
    newSocket.on("session:deleted", (data: { sessionId: string }) => {
      setSessions((prev) => prev.filter((s) => s._id !== data.sessionId));
      toast.warning("Une session a été supprimée.");
    });

    return () => { newSocket.disconnect(); };
  }, [dashboardId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDogClick = (dogId: string) => {
    setSelectedDogId(dogId);
    // setView("dogDetails"); // Removed as setView is no longer available
  };

  const handleSessionCreated = (newSession: Session) => {
    setSessions((prev) => {
      if (prev.some(s => s._id === newSession._id)) {
        return prev; // Prevent adding duplicates if WebSocket event arrived first.
      }
      return [...prev, newSession].sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
    });
  };

  const handleSessionUpdated = (updatedSession: Session) => {
    setSessions((prev) =>
      prev.map((s) => (s._id === updatedSession._id ? updatedSession : s))
    );
    if (selectedSession?._id === updatedSession._id) setSelectedSession(updatedSession);
  };

  const openCreateSessionForm = (date?: Date) => {
    setSessionToEdit(null);
    setInitialDateForNewSession(date);
    setIsSessionFormOpen(true);
  };

  const handleSelectSession = (sessionId: string) => {
    const session = sessions.find(s => s._id === sessionId);
    if (session) setSelectedSession(session);
  };

  const handleDragStart = (event: any) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = async (event: any) => {
    const { over, active } = event;
    setActiveDragId(null); // Clear the active drag overlay
    if (!over || !active) return;

    const dogId = active.id;
    const sessionId = over.id;

    const session = sessions.find(s => s._id === sessionId);
    if (!session) return;

    // Prevent adding the same dog twice
    if (session.plannedDogs.some(pd => pd.dog?._id === dogId)) {
      toast.info("Ce chien est déjà dans la session.");
      return;
    }

    const newPlannedDogs = [...session.plannedDogs.map(pd => ({ dog: pd.dog._id, objective: pd.objective })), { dog: dogId, objective: '' }];

    try {
      const updatedSession = await updateSession(sessionId, { plannedDogs: newPlannedDogs });
      handleSessionUpdated(updatedSession);
      toast.success(`Chien ajouté à la session du ${new Date(updatedSession.startTime).toLocaleDateString()}.`);
    } catch (error) {
      toast.error("Erreur lors de l'ajout du chien à la session.");
    }
  };

  const handleRemoveDogFromSession = async (sessionId: string, dogId: string) => {
    const session = sessions.find(s => s._id === sessionId);
    if (!session) return;

    const dogName = session.plannedDogs.find(pd => pd.dog?._id === dogId)?.dog.name || "le chien";

    const newPlannedDogs = session.plannedDogs
      .filter(pd => pd.dog?._id !== dogId)
      .map(pd => ({ dog: pd.dog._id, objective: pd.objective }));

    try {
      const updatedSession = await updateSession(sessionId, { plannedDogs: newPlannedDogs });
      handleSessionUpdated(updatedSession);
      toast.success(`${dogName} a été retiré de la session.`);
    } catch (error) {
      toast.error(`Erreur lors du retrait de ${dogName}.`);
    }
  };

  const draggedDog = activeDragId ? trainerDogs.find(d => d._id === activeDragId) : null;
  if (loading || !dashboard || !user) return <div>Chargement du tableau de bord...</div>;

  const isOwner = dashboard.ownerId === user._id;
  const userPermission = dashboard.trainerIds.find((t) => t.userId._id === user._id)?.permission;
  const canEdit = isOwner || !!userPermission; // Anyone in the trainerIds list can edit.
  const upcomingSessions = sessions.filter(s => new Date(s.startTime) > new Date()).length;
  const completedSessions = sessions.length - upcomingSessions;

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header Section */}
      <header className="p-4 border-b bg-card">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button onClick={onBack} variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{dashboard.title}</h1>
                {!isOwner && <Badge variant="outline">{userPermission === 'editor' ? 'Éditeur' : 'Lecteur'}</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{dashboard.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ToggleGroup type="single" value={viewType} onValueChange={(value: "list" | "calendar") => value && setViewType(value)}>
              {viewType === "calendar" && <ToggleGroupItem value="list" aria-label="List view"><List className="h-4 w-4" /></ToggleGroupItem>}
              {viewType === "list" && <ToggleGroupItem value="calendar" aria-label="Calendar view"><CalendarDays className="h-4 w-4" /></ToggleGroupItem>}
            </ToggleGroup>
            {canEdit && <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsBatchFormOpen(true)}>Créer en lot</Button>
              <Button size="lg" onClick={() => openCreateSessionForm()}><Plus className="mr-2 h-4 w-4" /> Nouvelle Session</Button>
            </div>}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-2 pl-14 text-sm text-muted-foreground">
          <span>{sessions.length} sessions</span>
          <Badge variant="outline">{upcomingSessions} à venir</Badge>
          <Badge variant="outline">{completedSessions} terminées</Badge>
        </div>
      </header>

      {/* Content Area */}
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <main className="flex-1 p-6 overflow-hidden grid grid-cols-1 lg:grid-cols-6 gap-6">
          <div className="lg:col-span-5 overflow-y-auto">
            {viewType === "list" && (
              <SessionsList
                sessions={sessions}
                onDogClick={handleDogClick}
                onSessionClick={handleSelectSession}
                selectedSessionId={selectedSession?._id}
                onSessionUpdate={handleSessionUpdated}
                canEdit={canEdit}
                onRemoveDog={handleRemoveDogFromSession}
                dashboardTrainers={dashboardTrainers.map(t => t.userId)}
              />
            )}
            {viewType === "calendar" && (
              <SessionCalendar sessions={sessions} onSessionClick={(session) => setSelectedSession(session)} onEmptySlotClick={openCreateSessionForm} />
            )}
          </div>
          <div className="lg:col-span-1 overflow-y-auto">
            <AvailableDogsList dogs={trainerDogs} />
          </div>
        </main>
        <DragOverlay>
          {draggedDog ? (
            <DraggableDog dog={draggedDog} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Footer Row */}
      <footer className="p-2 border-t text-xs text-muted-foreground flex justify-between items-center">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-3 w-3" />
          <span>Dernière sync: {new Date(dashboard.lastSyncedAt || Date.now()).toLocaleString()}</span>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            <Share2 className="h-3 w-3" />
            <span>Partagé avec {dashboard.trainerIds.length - 1} éducateur(s)</span>
          </div>
        )}
      </footer>
      <SessionForm
        isOpen={isSessionFormOpen}
        onOpenChange={setIsSessionFormOpen}
        dashboardId={dashboardId}
        trainerDogs={trainerDogs}
        sessionToEdit={sessionToEdit}
        onSessionCreated={handleSessionCreated}
        onSessionUpdated={handleSessionUpdated}
        initialDate={initialDateForNewSession}
      />
      <BatchSessionForm
        isOpen={isBatchFormOpen}
        onOpenChange={setIsBatchFormOpen}
        dashboardId={dashboardId}
        dashboardTrainers={dashboardTrainers.map(t => t.userId)}
        onSessionsCreated={fetchData}
      />
    </div>
  );
};